import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Slider, SelectChangeEvent } from "@mui/material";
import SelectCustomizationComponent from "../components/CompoundDetailPage/SelectCustomizationComponent";
import { motion } from "framer-motion";
import {
  CircuitBoard,
  Cpu,
  Fan,
  HardDrive,
  Keyboard,
  MemoryStick,
  Monitor,
  Mouse,
  PcCase,
  Power,
  Wifi,
  Rocket,
  Microchip,
  CircleArrowLeft,
  RefreshCw,
} from "lucide-react";
import ProductComponentsDetail from "../components/CompoundDetailPage/ProductComponentsDetail";
import {
  getBasePVPPrice,
  getCurrentConfigurationResellerPrice,
  getStandardConfigurationResellerPrice,
  calculateResellerMarginPercentage,
  calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage,
  calculateNewMarginPercentageBasedOnBasePVP,
  calculateNewMarginPercentageBasedOnConfigurationPVP,
  calculateComponentSelectionDelta,
  getConfiguredPVPUpgradeDelta,
  toInteger,
} from "../helpers/PriceShopEngine";
import { API_URLS, OrderComponets } from "../App";
import api from "../hooks/api";
import ManageOffer from "../components/Offers/ManageOffer";
import ReviewsWidget from "../components/ReviewsWidget";
import PriceDisplayResellerView from "../components/CompoundDetailPage/PriceDisplayResellerView";
import PriceDisplayCommercialView from "../components/CompoundDetailPage/PriceDisplayCommercialView";
import Image from "../components/common/Image";
import { useLayoutContext } from "../context/LayoutContext";
import OfferProposal from "../model/OfferProposal";

const CompoundDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    tasks,
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
  } = useLayoutContext();
  const [compound, setCompound] = useState<Compound | null>(null);
  const [initialMargin, setInitialMargin] = useState<number>(0);
  const isAtInitialMargin = (value: number) =>
    Math.abs(value - initialMargin) < 0.05;
  const [margin, setMargin] = useState<number | null>(null);
  const [customizationPrice, setCustomizationPrice] = useState<number>(0);
  const [basePricePVPInput, setBasePricePVPInput] = useState<number>(0);
  const [customizationPricePVPInput, setCustomizationPricePVPInput] =
    useState<number>(0);
  const [manualPriceEdit, setManualPriceEdit] = useState<boolean>(false);
  const [currentOffer, setCurrentOffer] = useState<OfferProposal | null>(null);
  const round2 = (n: number) => Math.round(n * 100) / 100;

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
    }
    if (id) {
      fetchPCDetails(id);
    }
  }, [isAuthenticated(), navigate, pathname, id]);

  const fetchPCDetails = useCallback(
    async (id: string) => {
      setError(null);
      setLoading(true);
      api
        .get(API_URLS.CATALOG + `/${encodeURIComponent(id)}`)
        .then((result) => {
          if (result.data) {
            const compoundData = { ...result.data } as Compound;
            // Normalize selected_components costs to deltas against their base
            // component — the API returns full reseller prices, but
            // getCurrentConfigurationResellerPrice adds them on top of the
            // (possibly promotional) base reseller price, causing double-counting.
            const normalizedSelected: Compound["selected_components"] = {};
            Object.entries(compoundData.selected_components || {}).forEach(
              ([type, entry]) => {
                if (!entry) return;
                const componentsForType = compoundData.components?.[type];
                if (!componentsForType) return; // skip keys not in catalog
                const selected = componentsForType?.find(
                  (c) => c.id === entry.id,
                );
                if (!selected) {
                  console.warn(
                    `[CompoundDetail] fetchPCDetails: component ${entry.id} (type ${type}) not found in catalog — delta set to 0`,
                  );
                }
                normalizedSelected[type] = {
                  id: entry.id,
                  cost: calculateComponentSelectionDelta(
                    componentsForType,
                    selected,
                  ),
                } as SelectedComponent;
              },
            );
            compoundData.selected_components = normalizedSelected;
            const initialMarginPercentage =
              toInteger(
                calculateResellerMarginPercentage(compoundData) * 10000,
              ) / 100;
            setCompound(compoundData);
            setInitialMargin(initialMarginPercentage);
            setCustomizationPrice(
              getCurrentConfigurationResellerPrice(compoundData),
            );
            setMargin(initialMarginPercentage);
          }
        })
        .catch((err) => {
          setError(
            err.message || "An error occurred while fetching PC details.",
          );
          console.warn(err);
        })
        .finally(() => setLoading(false));
    },
    [setError, setLoading],
  );

  const createOfferProposalInstance = useCallback(() => {
    if (!compound) {
      setError("Detalhes da configuração ou proposta não disponíveis.");
      return null;
    }
    const offerMargin =
      typeof margin === "number" && Number.isFinite(margin) ? margin : null;
    const effectiveMargin = offerMargin ?? initialMargin;
    const adjustedBase = !isAtInitialMargin(effectiveMargin)
      ? calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage(
          compound,
          effectiveMargin,
        )
      : getBasePVPPrice(compound);
    // Customizations are priced at the catalog PVP delta (matches the
    // storefront); only the base compound reacts to the margin slider.
    const upgradeDelta = getConfiguredPVPUpgradeDelta(compound);
    const adjustedValue = adjustedBase + upgradeDelta;
    return {
      client_type: "end_customer",
      client: {
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        zip_code: "",
        vat: "",
      },
      notes: [],
      compound: {
        id: compound.id,
        name: compound.name,
        price: {
          value: adjustedValue,
          resell: getCurrentConfigurationResellerPrice(compound),
          margin: offerMargin,
          without_vat: adjustedValue / (1 + compound.price.vat_rate),
          currency: compound.price.currency,
          vat_rate: compound.price.vat_rate,
          cost: getCurrentConfigurationResellerPrice(compound),
        },
        components: compound.components,
        selected_components: compound.selected_components,
      },
      status: "in_progress",
    } as unknown as OfferProposal;
  }, [compound, margin, initialMargin, setError]);

  const updateOfferProposal = useCallback(() => {
    if (!compound || !currentOffer) {
      setError("Detalhes da configuração ou proposta não disponíveis.");
      return null;
    }
    const offerMargin =
      typeof margin === "number" && Number.isFinite(margin) ? margin : null;
    const effectiveMargin = offerMargin ?? initialMargin;
    const adjustedBase = !isAtInitialMargin(effectiveMargin)
      ? calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage(
          compound,
          effectiveMargin,
        )
      : getBasePVPPrice(compound);
    // Customizations are priced at the catalog PVP delta (matches the
    // storefront); only the base compound reacts to the margin slider.
    const upgradeDelta = getConfiguredPVPUpgradeDelta(compound);
    const adjustedValue = adjustedBase + upgradeDelta;
    return {
      ...currentOffer,
      compound: {
        id: compound.id,
        price: {
          value: adjustedValue,
          resell: getCurrentConfigurationResellerPrice(compound),
          margin: offerMargin,
          without_vat: adjustedValue / (1 + compound.price.vat_rate),
          currency: compound.price.currency,
          vat_rate: compound.price.vat_rate,
          cost: getCurrentConfigurationResellerPrice(compound),
        },
        components: compound.components,
        selected_components: compound.selected_components,
      },
    } as OfferProposal;
  }, [compound, margin, initialMargin, currentOffer, setError]);

  const hasCustomization = () => {
    return Object.values(compound?.components ?? {}).some((components) =>
      components.some(
        (c) => c.selected && !c.baseComponent && c.id !== "empty_option",
      ),
    );
  };

  useEffect(() => {
    if (manualPriceEdit) {
      console.warn(
        "Manual price edit is enabled, skipping price update using margin calculations.",
      );
      return;
    }
    if (!compound) {
      console.warn("PC data is not available, using initial values.");
      setBasePricePVPInput(0);
      setCustomizationPricePVPInput(0);
      return;
    }
    // Fallback to initialMargin when margin is 0/NaN so that the price effect
    // always runs. Previously `if (!margin) return;` left the displayed PVP
    // stale whenever margin was unset, which masked option-selection changes.
    const effectiveMargin = margin ?? initialMargin;

    setCustomizationPrice(getCurrentConfigurationResellerPrice(compound));
    // Campaign-aware margin: the base PVP derives from the promotional
    // reseller price, so no extra promo ratio is applied on top.
    const promoAdjustedBase = !isAtInitialMargin(effectiveMargin)
      ? calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage(
          compound,
          effectiveMargin,
        )
      : getBasePVPPrice(compound);
    setBasePricePVPInput(round2(promoAdjustedBase));
    // Customizations are priced at the catalog PVP delta exactly like the
    // storefront (lib/storefront/compound.ts), independent of the margin slider.
    const upgradeDelta = getConfiguredPVPUpgradeDelta(compound);
    if (hasCustomization()) {
      setCustomizationPricePVPInput(round2(promoAdjustedBase + upgradeDelta));
    } else {
      setCustomizationPricePVPInput(0);
    }
  }, [margin, compound, initialMargin, manualPriceEdit]);

  const handleSliderChange = (_: Event, newValue: number) => {
    if (manualPriceEdit) return;
    const min = Math.min(0.1, initialMargin);
    if (newValue < min || newValue > 99.9) {
      setError(`Margin must be between ${min} and 99.9.`);
      return;
    }
    if (isNaN(newValue)) {
      setError("Invalid margin value.");
      return;
    }
    setMargin(newValue);
  };

  const handleChangeComponent = (
    event: SelectChangeEvent,
    componentType: string,
  ) => {
    setCompound((prev) => {
      if (!prev) return null;
      const selectedValue = event.target.value;
      const updatedComponents = prev.components[componentType].map((c) =>
        c.id === selectedValue
          ? { ...c, selected: true }
          : { ...c, selected: false },
      ) as Compound["components"][string];
      const selectedComponent = prev.components[componentType].find(
        (c) => c.id === selectedValue,
      );
      // Cost stored in selected_components must be the reseller delta
      // against the base component (no VAT) — getCurrentConfigurationResellerPrice
      // starts from the (possibly promotional) base reseller price (also no VAT)
      // and sums these deltas.
      // Previously we stored price.value (PVP with VAT), mixing units and
      // double-counting the base component.
      const deltaCost = calculateComponentSelectionDelta(
        prev.components[componentType],
        selectedComponent,
      );

      const newSelectedComponents = { ...prev.selected_components };
      if (selectedComponent && selectedValue !== "empty_option") {
        newSelectedComponents[componentType] = {
          id: selectedValue,
          cost: deltaCost,
        };
      } else {
        delete newSelectedComponents[componentType];
      }

      return {
        ...prev,
        components: { ...prev.components, [componentType]: updatedComponents },
        selected_components: newSelectedComponents,
      };
    });
  };

  const handleShowFullDetails = useCallback(() => {
    if (!compound) {
      setError("Detalhes da configuração não disponíveis.");
      return;
    }
    setModalScreen({
      state: "big-view",
      title: "Detalhes da Configuração",
      children: <ProductComponentsDetail pcConfig={compound} />,
      onClose: () => {
        setModalScreen(null);
      },
    });
  }, [compound, setError, setModalScreen]);

  const handleShowCreateProposal = useCallback(() => {
    if (!compound) {
      setError("Detalhes da configuração não disponíveis.");
      return;
    }
    const newProposal = createOfferProposalInstance();
    if (!newProposal) {
      setError(
        "Não foi possível criar a proposta. Verifique os dados da configuração.",
      );
      return;
    }
    setModalScreen({
      state: "edit",
      title: `Criar Proposta: ${newProposal.compound.id}`,
      childrenControlOverflow: true,
      children: <ManageOffer offer={newProposal} initialUpdatedOffer={false} />,
      onClose: () => {
        setModalScreen(null);
      },
    });
  }, [setError, setModalScreen, compound, createOfferProposalInstance]);

  const handleShowUpdateProposal = useCallback(() => {
    if (!compound || !currentOffer) {
      setError("Detalhes da configuração ou proposta não disponíveis.");
      return;
    }
    const updatedProposal = updateOfferProposal();
    if (!updatedProposal) {
      setError(
        "Não foi possível atualizar a proposta. Verifique os dados da configuração.",
      );
      return;
    }
    setModalScreen({
      state: "edit",
      title: `Alterar Proposta: #${updatedProposal.external_id}`,
      childrenControlOverflow: true,
      children: (
        <ManageOffer offer={updatedProposal} initialUpdatedOffer={true} />
      ),
      onClose: () => {
        setModalScreen(null);
      },
    });
    setCurrentOffer(null); // Clear current offer after showing the modal
  }, [setError, setModalScreen, compound, currentOffer, updateOfferProposal]);

  const handleBasePriceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!compound) return;
    if (manualPriceEdit) {
      const value = parseFloat(event.target.value);
      if (isNaN(value) || value <= 0) {
        setError("Base price must be a positive number.");
        return;
      }
      setBasePricePVPInput(value);
      const newMargin = calculateNewMarginPercentageBasedOnBasePVP(
        compound,
        value,
      );
      setMargin(newMargin);
      console.log("new margin: ", newMargin);
    }
    event.preventDefault();
  };

  const handleCustomizationPriceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!compound) return;
    if (manualPriceEdit) {
      const value = parseFloat(event.target.value);
      if (isNaN(value) || value <= 0) {
        setError("Customization price must be a positive number.");
        return;
      }
      setCustomizationPricePVPInput(value);
      const newMargin = calculateNewMarginPercentageBasedOnConfigurationPVP(
        compound,
        value,
      );
      setMargin(Math.round(newMargin * 10000) / 100);
    }
    event.preventDefault();
  };

  const handleManualEdit = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    console.log("Manual price edit enabled");
    setManualPriceEdit(true);
  };

  const handleAutomaticEdit = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (margin != null && margin < Math.min(initialMargin, 0) - 0.01) {
      console.warn("Margin is negative, cannot reset prices.");
      alert(
        "A margem não pode ser inferior à margem da campanha, alterado para o valor inicial.",
      );
      setMargin(initialMargin);
    }
    console.log("Manual price edit disabled");
    setManualPriceEdit(false);
    if (!compound || margin === null) {
      console.warn("PC data or margin is not available, cannot reset prices.");
      return;
    }
    const effectiveMargin = margin ?? initialMargin;
    // Customizations are priced at the catalog PVP delta (matches the
    // storefront); only the base compound reacts to the margin slider.
    const autoUpgradeDelta = getConfiguredPVPUpgradeDelta(compound);
    const autoPromoBase = !isAtInitialMargin(effectiveMargin)
      ? calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage(
          compound,
          effectiveMargin,
        )
      : getBasePVPPrice(compound);
    setBasePricePVPInput(round2(autoPromoBase));
    if (hasCustomization()) {
      setCustomizationPricePVPInput(round2(autoPromoBase + autoUpgradeDelta));
    } else {
      setCustomizationPricePVPInput(0);
    }
  };

  useEffect(() => {
    if (tasks.length > 0) {
      const changeOffersTasks = tasks.filter(
        (t) => t.taskType === "offer::change",
      );
      if (changeOffersTasks.length > 0) {
        const openTaskProposalToCompound = changeOffersTasks.find(
          (t) => t.getContext().offerProposal.compound.id === compound?.id,
        );
        if (openTaskProposalToCompound) {
          setCurrentOffer(
            openTaskProposalToCompound.getContext().offerProposal,
          );
          const offerCompound =
            openTaskProposalToCompound.getContext().offerProposal.compound;
          const newVersion = {
            ...compound,
            selected_components: Object.entries(
              offerCompound.selected_components || {},
            ).reduce((acc: Record<string, any>, [key, value]) => {
              // @ts-ignore
              if (value && typeof value.id === "string") {
                //@ts-ignore
                acc[key] = { ...value, id: value.id };
              }
              return acc;
            }, {}),
          } as Compound;
          // Normalize offer's selected_components costs to deltas against
          // the base component — the offer API returns full reseller prices
          // but getCurrentConfigurationResellerPrice expects deltas.
          // The offer API may include display-name keys (e.g. "Processador")
          // alongside snake_case keys — skip anything not in the catalog.
          const normalizedSelected: Compound["selected_components"] = {};
          Object.entries(newVersion.selected_components || {}).forEach(
            ([type, entry]) => {
              if (!entry) return;
              const componentsForType = newVersion.components?.[type];
              if (!componentsForType) return; // skip display-name duplicates
              const selected = componentsForType?.find(
                (c) => c.id === entry.id,
              );
              if (!selected) {
                console.warn(
                  `[CompoundDetail] offer::change: component ${entry.id} (type ${type}) not found in catalog — delta set to 0`,
                );
              }
              normalizedSelected[type] = {
                id: entry.id,
                cost: calculateComponentSelectionDelta(
                  componentsForType,
                  selected,
                ),
              } as SelectedComponent;
            },
          );
          newVersion.selected_components = normalizedSelected;
          // Update components with selected state based on offerCompound
          Object.entries(newVersion.components).map((item) => {
            const selectedComponent =
              offerCompound.selected_components[item[0]];
            if (selectedComponent) {
              item[1].forEach((component) => {
                component.selected = selectedComponent.id === component.id;
              });
            }
          });
          const margin = calculateNewMarginPercentageBasedOnConfigurationPVP(
            newVersion,
            offerCompound.price.value,
          );
          setCompound(newVersion);
          setMargin(margin);
        } else {
          setCurrentOffer(changeOffersTasks[0].getContext().offerProposal);
        }
      }
    } else {
      setCurrentOffer(null);
    }
  }, [tasks, compound?.id]);

  const currency = compound?.price.currency || "EUR";

  const icons = {
    Cpu: <Cpu className="inline mr-1" />,
    Gpu: <Microchip className="inline mr-1" />,
    CircuitBoard: <CircuitBoard className="inline mr-1" />,
    MemoryStick: <MemoryStick className="inline mr-1" />,
    HardDrive: <HardDrive className="inline mr-1" />,
    Fan: <Fan className="inline mr-1" />,
    PcCase: <PcCase className="inline mr-1" />,
    Wifi: <Wifi className="inline mr-1" />,
    Monitor: <Monitor className="inline mr-1" />,
    Keyboard: <Keyboard className="inline mr-1" />,
    Mouse: <Mouse className="inline mr-1" />,
    Power: <Power className="inline mr-1" />,
    Rocket: <Rocket className="inline mr-1" />,
  };

  const showCosts = function (): boolean {
    return userSettings?.generalSettings?.show_cost_price ?? false;
  };

  if (!isAuthenticated()) {
    return <div>You must be logged in to edit items.</div>;
  }

  return !compound ? (
    <>
      <div>Carregando detalhes do PC...</div>
    </>
  ) : (
    <>
      <div className="container mx-auto not-printable">
        <div className="p-4 overflow-auto">
          <div className="text-2xl font-bold mb-4 pt-5 items-center">
            <Link
              to={`/`}
              className="not-printable"
              onClick={(e) => {
                e.preventDefault();
                if (currentOffer) {
                  navigate("/");
                } else {
                  history.back();
                }
              }}
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="ml-5 mr-5 text-red-500 hover:text-red-700 flex items-center"
              >
                {" "}
                {currentOffer ? (
                  <>
                    <RefreshCw className="w-7 h-7 inline-block" />
                    <span className="pl-2">Escolher outro computador</span>
                  </>
                ) : (
                  <>
                    <CircleArrowLeft className="w-7 h-7 inline-block" />
                    <span className="pl-2">Voltar</span>
                  </>
                )}
              </motion.button>
            </Link>
          </div>
          <div className="flex items-start flex-col md:flex-row pb-30">
            {compound.image && (
              <div className="w-full md:shrink-0 md:w-1/3 md:sticky md:top-0 p-4 not-printable">
                <Image
                  src={`https://placehold.co/600x600/B3B1A9/000000?text=${compound.description}`}
                  loading="progressive"
                  dataSrc={compound.image}
                  alt={compound.name}
                  className="rounded-lg object-cover w-full"
                  sizes="600"
                  version={compound.version}
                />
              </div>
            )}
            <div className="flex-grow ml-0 md:ml-4 print-content">
              <div className="p-4 md:sticky configuration-header">
                <p className="text-xl font-extrabold">{compound.name}</p>
                {compound.description && (
                  <p className="text-xl  mb-2">{compound.description}</p>
                )}
                {Object.keys(compound.components).length > 0 &&
                  OrderComponets.map((key) => {
                    let component = compound.components[key[0]]?.find(
                      (c) => !c.optional,
                    );
                    if (component && component.name.trim() !== "") {
                      return (
                        <p
                          key={key[0]}
                          className="text-sm font-bold text-gray-400 p-1 not-printable"
                        >
                          {
                            // @ts-ignore
                            icons[key[1]] || null
                          }
                          {component.quantity > 1
                            ? `${component.quantity} x `
                            : ""}
                          {component.description}
                        </p>
                      );
                    }
                    return null;
                  })}

                {userSettings?.isCommercial() ||
                currentOffer?.client_type == "reseller" ? (
                  <PriceDisplayCommercialView
                    compound={compound}
                    currency={currency}
                    hasCustomization={hasCustomization}
                    customizationPrice={customizationPrice}
                    previousResellerPrice={getStandardConfigurationResellerPrice(
                      compound,
                    )}
                    basePricePVPInput={basePricePVPInput}
                    customizationPricePVPInput={customizationPricePVPInput}
                    handleManualEdit={handleManualEdit}
                    handleAutomaticEdit={handleAutomaticEdit}
                    handleBasePriceChange={handleBasePriceChange}
                    handleCustomizationPriceChange={
                      handleCustomizationPriceChange
                    }
                    allowEditPrices={!userSettings?.isNormalUser()}
                  />
                ) : (
                  <PriceDisplayResellerView
                    compound={compound}
                    currency={currency}
                    hasCustomization={hasCustomization}
                    customizationPrice={customizationPrice}
                    previousResellerPrice={getStandardConfigurationResellerPrice(
                      compound,
                    )}
                    basePricePVPInput={basePricePVPInput}
                    customizationPricePVPInput={customizationPricePVPInput}
                    handleManualEdit={handleManualEdit}
                    handleAutomaticEdit={handleAutomaticEdit}
                    handleBasePriceChange={handleBasePriceChange}
                    handleCustomizationPriceChange={
                      handleCustomizationPriceChange
                    }
                    allowEditPrices={!userSettings?.isNormalUser()}
                    showCostPrice={showCosts()}
                  />
                )}
                {(userSettings?.generalSettings?.show_margin_slider ?? false) &&
                  (userSettings?.generalSettings?.show_cost_price ?? false) &&
                  !!compound.price.margin && (
                    <p className="text-xs font-bold text-gray-400 reseller-margin not-printable">
                      Margem: {margin ?? initialMargin} %
                      <Slider
                        min={Math.min(0.1, initialMargin)}
                        max={99.9}
                        value={Math.max(
                          Math.min(0.1, initialMargin),
                          Math.min(99.9, margin ?? initialMargin),
                        )}
                        valueLabelDisplay="auto"
                        step={0.1}
                        valueLabelFormat={(value) => `${value} %`}
                        color={
                          (margin ?? initialMargin) >= initialMargin - 1 &&
                          (margin ?? initialMargin) <= initialMargin + 1
                            ? "success"
                            : (margin ?? initialMargin) > 100
                              ? "error"
                              : (margin ?? initialMargin) <
                                  initialMargin - initialMargin / 2
                                ? "warning"
                                : (margin ?? initialMargin) <
                                    initialMargin * 2 - initialMargin / 2
                                  ? "primary"
                                  : "secondary"
                        }
                        marks={
                          initialMargin >= 0.1 && initialMargin <= 99.9
                            ? [
                                {
                                  value: initialMargin,
                                  label: "Recomendado: " + initialMargin + " %",
                                },
                              ]
                            : []
                        }
                        onChange={handleSliderChange}
                      />
                    </p>
                  )}
              </div>
              {compound.allow_customization && (
                <>
                  <p className="text-xl font-bold text-gray-900 p-2 mt-2 not-printable text-center">
                    Personalize a sua configuração
                  </p>
                  <ul className="grid grid-cols-1 gap-4 p-4">
                    {Object.entries(compound.components).map(
                      ([componentType, components]) => {
                        return (
                          <SelectCustomizationComponent
                            key={componentType}
                            componentType={componentType}
                            components={components}
                            selectedComponent={
                              compound.selected_components?.[componentType] ??
                              null
                            }
                            margin={margin ?? initialMargin} //use initialMargin
                            vatRate={compound.price.vat_rate}
                            currency={currency}
                            onChange={handleChangeComponent}
                            showCosts={showCosts()}
                          />
                        );
                      },
                    )}
                  </ul>
                </>
              )}

              <div className="flex is-justify-content-center mt-4 not-printable">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2"
                  onClick={handleShowFullDetails}
                >
                  Mais Detalhes
                </motion.button>
                {isAuthenticated() &&
                  (currentOffer ? (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-red-400 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                      onClick={handleShowUpdateProposal}
                    >
                      Alterar Proposta Actual
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-red-400 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                      onClick={handleShowCreateProposal}
                    >
                      Criar Proposta
                    </motion.button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 not-printable">
        <ReviewsWidget
          entityId={compound.id}
          entityType="product"
          customerName={userSettings?.profile?.firstName || undefined}
          customerEmail={userSettings?.profile?.email || undefined}
          authToken={localStorage.getItem("authToken") || undefined}
          locale="pt"
        />
      </div>
    </>
  );
};

export default CompoundDetailPage;
